import IAppEvents from '@app/lib/IAppEvents';
import IAppLogger from '@app/lib/IAppLogger';
import {
  ChangeAction,
  CurrentUserChangedSubscription,
} from '@api/generated/graphql';
import FeatureFlagModule, { FeatureFlagKeys } from '@modules/FeatureFlagModule';
import type { SubscriptionRetryOutcome } from '@modules/DiagnosticsModule';
import { AuthEvents } from './events';
import { currentUserChangedSubscription } from './queries';
import { authState } from '../state';
import _ from 'lodash';

/**
 * Manages real-time user data synchronization via GraphQL subscriptions
 * with complementary periodic polling.
 *
 * Responsibilities:
 * - Set up and maintain GraphQL subscription for user changes
 * - Handle subscription errors with exponential backoff retry
 * - Maintain 5-minute periodic polling alongside subscription for updates
 *   not covered by subscription (e.g., permissions changes)
 * - Deduplicate concurrent refresh requests
 * - Emit auth.user.updated events when user data updates
 */
export type SubscriptionRetryListener = (
  outcome: SubscriptionRetryOutcome,
  attempt: string,
) => void;

export class UserSubscriptionManager {
  private readonly logger: IAppLogger;
  private readonly events: IAppEvents;
  private readonly onRetry: SubscriptionRetryListener;

  private userSubscription: { unsubscribe: () => void } | undefined;
  private subscriptionRetryCount = 0;
  private retriesExhaustedReported = false;
  private readonly MAX_RETRY_ATTEMPTS = 5;
  private readonly RETRY_DELAYS = [1000, 2000, 5000, 10000, 30000];
  private readonly PERIODIC_POLLING_INTERVAL_MS = 300000; // 5 minutes (complement to subscriptions)
  private readonly FALLBACK_POLLING_INTERVAL_MS = 60000; // 60 seconds (when subscriptions disabled)
  private readonly FEATURE_FLAGS_TIMEOUT_MS = 5000; // 5 seconds timeout for feature flag initialization
  private readonly STABLE_SUBSCRIPTION_MS = 60000; // error-free time after which a retried subscription counts as recovered; twice the longest retry delay

  private periodicPollingInterval: NodeJS.Timeout | undefined;
  private userRefreshPromise: Promise<void> | null = null;
  private retryTimeout: NodeJS.Timeout | undefined;
  private stableTimeout: NodeJS.Timeout | undefined;
  private cleanupCount = 0;

  constructor(
    logger: IAppLogger,
    events: IAppEvents,
    onRetry: SubscriptionRetryListener = () => {},
  ) {
    this.logger = logger;
    this.events = events;
    this.onRetry = onRetry;
  }

  /**
   * Waits for feature flags to initialize before proceeding.
   * Includes a timeout to prevent indefinite waiting if LaunchDarkly fails to initialize.
   *
   * This ensures feature flags are loaded before checking their values, preventing
   * race conditions during authentication when flags may not be ready yet.
   *
   * @private
   * @returns Promise that resolves when flags are ready or timeout is reached
   */
  private async waitForFeatureFlagsInitialization(): Promise<void> {
    // If flags are already ready, continue immediately
    if (FeatureFlagModule.areFlagsReady) {
      this.logger.debug('User subscription: Feature flags already initialized');
      return;
    }

    this.logger.debug(
      'User subscription: Waiting for feature flags to initialize...',
    );

    // Wait for flags with timeout for resilience
    let timeoutId: NodeJS.Timeout | undefined;
    let listener: (() => void) | undefined;

    try {
      const flagsReadyPromise = new Promise<void>((resolve) => {
        listener = () => {
          this.logger.debug('User subscription: Feature flags initialized');
          resolve();
        };
        this.events.on('featureflags.initialized', listener);
      });

      const timeoutPromise = new Promise<void>((resolve) => {
        timeoutId = setTimeout(() => {
          this.logger.warn(
            `User subscription: Feature flag initialization timeout after ${this.FEATURE_FLAGS_TIMEOUT_MS}ms, proceeding with default behavior`,
          );
          resolve();
        }, this.FEATURE_FLAGS_TIMEOUT_MS);
      });

      await Promise.race([flagsReadyPromise, timeoutPromise]);
    } finally {
      // Clean up: remove event listener and clear timeout regardless of which promise won
      if (listener) {
        this.events.off('featureflags.initialized', listener);
      }
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    }
  }

  /**
   * Schedules an exponential-backoff retry of subscription setup.
   *
   * Shared by the in-`next` `result.error` branch and the rxjs `error:` callback
   * so both recoverable and non-recoverable failures follow the same retry path.
   * v4's `next` is non-terminal, so one failure can deliver several error
   * frames; while a retry is pending or its setup() is still running, later
   * frames belong to the same failure and don't spend another attempt.
   * setup() clears `retryTimeout` once it replaces the old subscription.
   *
   * @private
   */
  private scheduleSubscriptionRetry(): void {
    this.clearStableTimeout();
    if (this.retryTimeout) {
      return;
    }
    if (this.subscriptionRetryCount < this.MAX_RETRY_ATTEMPTS) {
      const delay = this.RETRY_DELAYS[this.subscriptionRetryCount];
      this.subscriptionRetryCount++;
      this.onRetry('scheduled', String(this.subscriptionRetryCount));

      this.logger.info(
        `User subscription: retrying subscription in ${delay}ms (attempt ${this.subscriptionRetryCount}/${this.MAX_RETRY_ATTEMPTS})`,
      );

      this.retryTimeout = setTimeout(() => {
        if (authState.user?.id) {
          this.setup(authState.user.id).catch((error) => {
            this.retryTimeout = undefined;
            this.logger.error('User subscription: Retry setup failed', error);
          });
        } else {
          this.retryTimeout = undefined;
        }
      }, delay);
    } else {
      this.logger.warn(
        'User subscription: Max retry attempts reached, will continue with periodic polling only',
      );
      if (!this.retriesExhaustedReported) {
        this.retriesExhaustedReported = true;
        this.onRetry('exhausted', '');
      }
      // Periodic polling continues to handle updates
    }
  }

  /**
   * Sets up GraphQL subscription for the authenticated user.
   * Should be called after successful authentication.
   *
   * @param userId - The ID of the authenticated user
   */
  public async setup(userId: string): Promise<void> {
    if (!userId) {
      this.logger.warn(
        'User subscription: No user ID provided for subscription setup',
      );
      return;
    }

    // Wait for feature flags to initialize to avoid race conditions
    const cleanupCount = this.cleanupCount;
    await this.waitForFeatureFlagsInitialization();
    if (this.cleanupCount !== cleanupCount) {
      // Signed out while waiting
      return;
    }

    // Check if GraphQL subscriptions are enabled via feature flag
    const subscriptionsEnabled = FeatureFlagModule.isEnabled(
      FeatureFlagKeys.featureGraphqlSubscriptionsEnabled,
    );

    if (!subscriptionsEnabled) {
      this.logger.info(
        'User subscription: GraphQL subscriptions disabled by feature flag, using 60-second polling',
      );
      this.clearRetryTimeout();
      this.clearStableTimeout();
      this.userSubscription?.unsubscribe();
      this.userSubscription = undefined;
      // Start 60-second polling as primary mechanism when subscriptions disabled
      this.startPeriodicPolling(this.FALLBACK_POLLING_INTERVAL_MS);
      return;
    }

    this.clearRetryTimeout();
    this.clearStableTimeout();

    // Clean up existing subscription before creating new one
    if (this.userSubscription) {
      this.logger.debug(
        'User subscription: Cleaning up existing subscription before creating new one',
      );
      this.userSubscription.unsubscribe();
      this.userSubscription = undefined;
    }

    try {
      // Import getClient here to avoid circular dependency with @api/client
      const { getClient } = await import('@api/client');
      const client = getClient();
      let failedDuringSubscribe = false;
      this.userSubscription = client
        .subscribe({
          query: currentUserChangedSubscription,
          variables: {
            actions: [ChangeAction.Updated],
            userId: userId,
          },
        })
        .subscribe({
          next: (result) => {
            if (result.error) {
              // Transient WebSocket errors are expected; exponential backoff retry
              // will recover, and polling continues as fallback throughout.
              this.logger.warn(
                'User subscription: WebSocket subscription received an error',
                {
                  error: result.error.message ?? result.error,
                  userId: authState.user?.id,
                  retryCount: this.subscriptionRetryCount,
                },
              );

              failedDuringSubscribe = true;
              this.scheduleSubscriptionRetry();
              return;
            }

            this.logger.debug('User subscription: message received', {
              userId: result.data?.userChanged?.data?.id,
              action: result.data?.userChanged?.action,
            });

            this.resetRetryState();

            this.handleUserChange(result.data);
          },
          // v4: GraphQL errors arrive via next({error}); the rxjs error channel
          // only fires for non-recoverable network failures that terminate the
          // Observable. Re-subscribe via the same exponential backoff path so the
          // stream recovers without waiting for the 5-minute polling fallback.
          error: (error) => {
            this.logger.warn(
              'User subscription: Subscription terminated by non-recoverable error',
              {
                error: error?.message ?? error,
                userId: authState.user?.id,
                retryCount: this.subscriptionRetryCount,
              },
            );
            failedDuringSubscribe = true;
            this.scheduleSubscriptionRetry();
          },
        });

      // Skip when subscribe() already failed synchronously.
      if (this.subscriptionRetryCount > 0 && !failedDuringSubscribe) {
        this.stableTimeout = setTimeout(() => {
          this.stableTimeout = undefined;
          this.resetRetryState();
        }, this.STABLE_SUBSCRIPTION_MS);
      }

      this.logger.info(
        'User subscription: Subscription setup complete, establishing WebSocket connection',
        {
          userId: userId,
          retryCount: this.subscriptionRetryCount,
        },
      );
    } catch (error) {
      this.logger.error(
        'User subscription: Error setting up subscription:',
        error,
      );

      this.scheduleSubscriptionRetry();
    }

    // Start periodic polling to ensure we catch updates not covered by subscription
    this.startPeriodicPolling();
  }

  /**
   * Cleans up subscription and periodic polling mechanisms.
   * Should be called on signout.
   */
  public cleanup(): void {
    this.cleanupCount++;
    if (this.userSubscription) {
      this.userSubscription.unsubscribe();
      this.userSubscription = undefined;
    }
    this.clearRetryTimeout();
    this.clearStableTimeout();
    this.resetRetryState();
    clearInterval(this.periodicPollingInterval);
    this.periodicPollingInterval = undefined;
    this.userRefreshPromise = null;
  }

  private resetRetryState(): void {
    this.subscriptionRetryCount = 0;
    this.retriesExhaustedReported = false;
  }

  private clearRetryTimeout(): void {
    if (this.retryTimeout) {
      clearTimeout(this.retryTimeout);
      this.retryTimeout = undefined;
    }
  }

  private clearStableTimeout(): void {
    if (this.stableTimeout) {
      clearTimeout(this.stableTimeout);
      this.stableTimeout = undefined;
    }
  }

  /**
   * Handles app wake events.
   * Re-establishes subscription and triggers immediate user refresh.
   */
  public async handleWake(): Promise<void> {
    // If subscription failed or doesn't exist, try to re-establish it
    if (!this.userSubscription && authState.user?.id) {
      this.logger.info(
        'User subscription: Re-establishing subscription after wake',
      );
      await this.setup(authState.user.id);
    }

    // Trigger immediate refresh to get latest data after wake
    // (don't wait for next periodic polling tick)
    this.logger.debug('User subscription: Refreshing user state after wake');
    await this.refreshUser();
  }

  /**
   * Manually triggers a user state refresh.
   * Can be called externally to force a refresh.
   */
  public async refreshUser(): Promise<void> {
    await this.refreshUserState();
  }

  /**
   * Handles user change events from the GraphQL subscription.
   * This is a notification-only handler - it doesn't process the subscription
   * payload directly, but instead fetches the latest user data via getCurrentUser().
   *
   * @private
   * @param data - The subscription notification (contains minimal data)
   */
  private async handleUserChange(
    data: CurrentUserChangedSubscription | null | undefined,
  ): Promise<void> {
    if (!data?.userChanged?.data || !authState.user) {
      this.logger.warn(
        'User subscription: Invalid subscription data received',
        {
          hasData: !!data,
        },
      );
      return;
    }

    const updatedUser = data.userChanged.data;

    // Validate required fields
    if (!updatedUser.id) {
      this.logger.error('User subscription: data missing user ID');
      return;
    }

    // Only process changes for the current user (double-check even though filtered server-side)
    if (updatedUser.id !== authState.user.id) {
      this.logger.warn(
        'User subscription: Received update for different user',
        {
          receivedUserId: updatedUser.id,
          currentUserId: authState.user.id,
        },
      );
      return;
    }

    this.logger.debug(
      'User subscription: change notification received, fetching latest data',
      {
        userId: updatedUser.id,
        action: data.userChanged.action,
      },
    );

    // Fetch latest user data and update state
    // Uses deduplication to prevent race conditions
    await this.refreshUserState();
  }

  /**
   * Refreshes the user state by fetching the current user data and
   * updating the local state if any changes are detected. Emits an 'auth.user.updated'
   * event if there are changes in the user's seat level, personal line,
   * permissions, or outbound call allowance.
   *
   * Uses promise deduplication to ensure only one refresh happens at a time,
   * even when multiple refresh requests are made simultaneously.
   *
   * @private
   * @returns {Promise<void>} A promise that resolves when the user state has been refreshed.
   */
  private async refreshUserState(): Promise<void> {
    // If a refresh is already in progress, wait for it
    if (this.userRefreshPromise) {
      this.logger.debug(
        'User subscription: refresh already in progress, waiting...',
      );
      await this.userRefreshPromise;
      return;
    }

    // Start a new refresh and store the promise
    this.userRefreshPromise = this._performUserRefresh().finally(() => {
      this.userRefreshPromise = null;
    });

    await this.userRefreshPromise;
  }

  /**
   * Performs the actual user state refresh by fetching current user data
   * and updating the local state. Batches all changes and emits a single
   * 'auth.user.updated' event if any changes were detected.
   *
   * @private
   * @async
   * @returns {Promise<void>} A promise that resolves when the refresh is complete.
   */
  private async _performUserRefresh(): Promise<void> {
    if (!authState.user) {
      // Expected race condition: polling interval fires between logout and cleanup()
      this.logger.debug('User subscription: No user to refresh, skipping');
      return;
    }

    try {
      // refresh the user and check if anything has changed
      // Import getCurrentUser lazily to avoid circular dependency
      const getCurrentUser = (await import('./getCurrentUser')).default;
      const refreshedUser = await getCurrentUser(true);
      let hasChanges = false;

      // Check and update seat level
      if (refreshedUser.seatLevel !== authState.user.seatLevel) {
        authState.user.seatLevel = refreshedUser.seatLevel;
        hasChanges = true;
      }

      // Check and update personal line
      if (!_.isEqual(refreshedUser.personalLine, authState.user.personalLine)) {
        authState.user.personalLine = refreshedUser.personalLine;
        hasChanges = true;
      }

      // Check and update e911Address independently
      if (!_.isEqual(refreshedUser.e911Address, authState.user.e911Address)) {
        authState.user.e911Address = refreshedUser.e911Address;
        hasChanges = true;
      }

      // Check and update permissions
      if (!_.isEqual(refreshedUser.permissions, authState.user.permissions)) {
        authState.user.permissions = refreshedUser.permissions;
        hasChanges = true;
      }

      // Check and update outbound calls allowed
      if (
        refreshedUser.outboundCallsAllowed !==
        authState.user.outboundCallsAllowed
      ) {
        authState.user.outboundCallsAllowed =
          refreshedUser.outboundCallsAllowed;
        hasChanges = true;
      }

      // Check and update call recording terms
      if (
        refreshedUser.hasAcceptedCallRecordingTerms !==
        authState.user.hasAcceptedCallRecordingTerms
      ) {
        authState.user.hasAcceptedCallRecordingTerms =
          refreshedUser.hasAcceptedCallRecordingTerms;
        hasChanges = true;
      }

      // Check and update exclusion list terms
      if (
        refreshedUser.hasAcceptedExclusionListTerms !==
        authState.user.hasAcceptedExclusionListTerms
      ) {
        authState.user.hasAcceptedExclusionListTerms =
          refreshedUser.hasAcceptedExclusionListTerms;
        hasChanges = true;
      }

      // Emit a single event if any changes were detected
      if (hasChanges) {
        this.events.emit(AuthEvents.USER_UPDATED, authState.user);
      }
    } catch (err) {
      // Transient failures (network, auth expiry) are expected; the next polling
      // tick or subscription retry will recover without user-visible impact.
      this.logger.warn('User subscription: Error refreshing user', err);
    }
  }

  /**
   * Starts periodic polling mechanism.
   * When subscriptions are enabled: 5-minute polling as complement to subscriptions
   * When subscriptions are disabled: 60-second polling as primary mechanism
   *
   * @param intervalMs - Optional polling interval in milliseconds. Defaults to 5 minutes.
   * @private
   */
  private startPeriodicPolling(
    intervalMs: number = this.PERIODIC_POLLING_INTERVAL_MS,
  ): void {
    // Clear any existing interval first
    if (this.periodicPollingInterval) {
      clearInterval(this.periodicPollingInterval);
    }

    const intervalSeconds = Math.floor(intervalMs / 1000);
    this.logger.info(
      `User subscription: Starting ${intervalSeconds}-second periodic polling`,
    );

    this.periodicPollingInterval = setInterval(() => {
      this.logger.debug('User subscription: Periodic polling tick');
      void this.refreshUserState();
    }, intervalMs);
  }
}
